import {Close} from "@mui/icons-material";
import {Box, Collapse, IconButton, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {alpha} from "@mui/material/styles";
import {useState} from "react";

/**
 * LsInfoBar — application-level generic info/context bar.
 *
 * Props:
 *  icon        {ReactNode}
 *  label       {string}
 *  description {string}                                         Required.
 *  chips       {Array<{ icon?: ReactNode, text: string }>}
 *  severity    {"info"|"success"|"warning"|"error"|"neutral"}   Default: "info"
 *  dismissible {boolean}                                        Default: true
 *  onDismiss   {function}
 */

const useSeverityMap = () => {
    const {palette} = useTheme();
    return {
        info: {
            color: palette.primary.main,
            bg: alpha(palette.primary.main, 0.05),
            border: alpha(palette.primary.main, 0.18)
        },
        success: {
            color: palette.success.main,
            bg: alpha(palette.success.main, 0.05),
            border: alpha(palette.success.main, 0.18)
        },
        warning: {
            color: palette.warning.main,
            bg: alpha(palette.warning.main, 0.05),
            border: alpha(palette.warning.main, 0.18)
        },
        error: {
            color: palette.error.main,
            bg: alpha(palette.error.main, 0.05),
            border: alpha(palette.error.main, 0.18)
        },
        neutral: {
            color: palette.status.legal,
            bg: alpha(palette.status.legal, 0.05),
            border: alpha(palette.status.legal, 0.18)
        },
    };
};

export default function LsInfoBar({
                                      icon,
                                      label,
                                      description,
                                      chips = [],
                                      severity = "info",
                                      dismissible = true,
                                      onDismiss,
                                  }) {
    const [visible, setVisible] = useState(true);
    const severityMap = useSeverityMap();
    const {color, bg, border} = severityMap[severity] ?? severityMap.info;

    const handleDismiss = () => {
        setVisible(false);
        onDismiss?.();
    };

    return (
        <Collapse in={visible} unmountOnExit>
            <Box
                sx={{
                    boxSizing: "border-box",
                    width: "100%",
                    borderBottom: `1px solid ${border}`,
                    bgcolor: bg,
                    px: 3,
                    py: "7px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexShrink: 0,
                    overflow: "hidden",
                }}
            >
                {/* Icon pill */}
                {icon && (
                    <Box
                        sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "6px",
                            bgcolor: color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.contrastText",
                            flexShrink: 0,
                        }}
                    >
                        {icon}
                    </Box>
                )}

                {/* Label */}
                {label && (
                    <>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                color,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                letterSpacing: "0.1px",
                            }}
                        >
                            {label}
                        </Typography>

                        {/* Separator dot */}
                        <Box
                            sx={{
                                width: 3,
                                height: 3,
                                borderRadius: "50%",
                                bgcolor: color,
                                opacity: 0.4,
                                flexShrink: 0,
                            }}
                        />
                    </>
                )}

                {/* Description */}
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: "0.72rem",
                        color: "text.secondary",
                        lineHeight: 1.5,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {description}
                </Typography>

                {/* Chips */}
                {chips.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 0.75,
                            flexShrink: 0,
                            ml: 1,
                            "@media (max-width: 700px)": {display: "none"},
                        }}
                    >
                        {chips.map((chip, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    px: "8px",
                                    py: "3px",
                                    borderRadius: "4px",
                                    border: `1px solid ${border}`,
                                    bgcolor: "background.paper",
                                    color,
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    letterSpacing: "0.2px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {chip.icon}
                                {chip.text}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Dismiss */}
                {dismissible && (
                    <IconButton
                        size="small"
                        onClick={handleDismiss}
                        aria-label="Dismiss"
                        sx={{
                            ml: 0.5,
                            flexShrink: 0,
                            width: 22,
                            height: 22,
                            color: "text.disabled",
                            "&:hover": {color: "text.secondary"},
                        }}
                    >
                        <Close sx={{fontSize: 14}}/>
                    </IconButton>
                )}
            </Box>
        </Collapse>
    );
}